import { Box, ColorButton, Typography, Stack, Link } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { graphql, getFragmentData } from '@packages/gql/generated/shopping';
import { useRouter } from 'next/router';
import { useIsBot } from '@packages/tracking';
import type { SetSelectedVariation } from '../../../types';
import { activeStyles, defaultStyles } from './styles';
import { getVariationFormattedLink } from '../../../helpers/getVariationFormattedLink';

const ColorTilesValuesFragment = graphql(/* GraphQL */ `
  fragment ColorTilesValuesFragment on ProductDimension {
    values {
      displayText
      image
      isSelected
      rgb
      sku
    }
  }
`);

export type ColorTilesDimensionProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof ColorTilesValuesFragment>;
  selectedVariation: string | null;
  onChange: SetSelectedVariation;
};

/**
 * ColorTilesDimension to choose a product color on Detailview
 * */
export const ColorTilesDimension = ({
  dimensionData,
  onChange,
  selectedVariation,
}: ColorTilesDimensionProps) => {
  const router = useRouter();
  const isBot = useIsBot();
  const dimension = getFragmentData(ColorTilesValuesFragment, dimensionData);
  const selectedColor = dimension.values.find((value) => value.isSelected);

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {selectedColor ? selectedColor.displayText : ''}
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.5} sx={{ mt: 0.75 }}>
        {dimension.values.map(({ sku, image, rgb, displayText, isSelected }) => {
          const isActive = isSelected || false;
          return (
            <ColorButton
              onClick={() => onChange(sku)}
              key={`colorBtn${sku}`}
              minimal={false}
              image={image || undefined}
              color={rgb ? `#${rgb}` : ''}
              name={displayText || ''}
              active={isActive}
              isSelected={selectedVariation === sku}
              sx={isActive ? activeStyles : defaultStyles}
              LinkComponent={Link}
              href={isBot ? getVariationFormattedLink(sku, router) : undefined}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
